import { Button, Col, Input, Modal, Popconfirm, Row, Select, Space, Table, Tabs, message } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marketplaces } from 'helpers';
import { addProductListUrl, deleteProductListUrl, getAllProductListUrls, getSupplierList, updateProductListUrl, updateSupplierName } from 'services';
import { Helmet } from 'react-helmet';
import PageNotFound from '../404';

export interface TSupplier {
  _id: string;
  domain: string;
}

export interface TSupplierSelectionState {
  label: string;
  value: string;
}

export interface TProductListUrlState {
  _id: string;
  supplier: string;
  url: string;
}

const initialProductListUrlState = {
  _id: "",
  supplier: "",
  url: "US",
}

const initialSelectedSupplierState = {
  label: "",
  value: "",
}

const ProductListURLs = () => {
  const [activedTab, setActivedTab] = useState<string | null>();
  const [dataTable, setDataTable] = useState<any>([]);
  const [productListUrl, setProductListUrl] = useState<string>("")
  const [updatedProductListUrl, setUpdatedProductListUrl] = useState<TProductListUrlState>(initialProductListUrlState)
  const [selectedSupplier, setSelectedSupplier] = useState<TSupplierSelectionState>(initialSelectedSupplierState)
  const [updatedSupplier, setUpdatedSupplier] = useState<TSupplierSelectionState>(initialSelectedSupplierState)
  const [suppliers, setSuppliers] = useState<TSupplierSelectionState[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [openEditProductListModal, setOpenEditProductListModal] = useState<boolean>(false)
  const [openEditSupplierNameModal, setOpenEditSupplierNameModal] = useState<boolean>(false)

  let { marketplace } = useParams();
  let navigate = useNavigate();

  useEffect(() => {
    setActivedTab(marketplace)
  }, [marketplace]);

  useEffect(() => {
    if (marketplace) {
      setLoading(true)
      getSupplierList(marketplace)
        .then(({ data }) => {
          const optList: TSupplierSelectionState[] = data.data.map((spl: TSupplier) => ({
            label: spl.domain,
            value: spl._id,
          }))

          if (optList.length > 0) {
            setSuppliers(_ => {
              setSelectedSupplier(optList[0])
              return optList
            })
          } else {
            message.warning("There are no suppliers in this marketplace")
          }
        })
        .catch(() =>
          message.error("Cannot get supplier list, please refresh page again")
        )
        .finally(() => setLoading(false))
    }
  }, [marketplace])

  useEffect(() => {
    if (activedTab) {
      setLoading(true)
      getAllProductListUrls(activedTab)
        .then(({ data }) => setDataTable(data.data))
        .catch(() =>
          message.error("Cannot get all product list URLs, please refresh page again")
        )
        .finally(() => setLoading(false))
    }
  }, [activedTab])

  if (!marketplace) {
    return <PageNotFound />
  }

  const handleOnChangeTab = (tabKey: string) => {
    navigate(`../manage-product-list-urls/${tabKey}`, { replace: true });
  };

  const handleOnClickAdd = () => {
    setLoading(true)
    const payload = {
      supplier: selectedSupplier.label,
      url: productListUrl,
      productUrlSelector: []
    }
    return addProductListUrl(marketplace as string, payload)
      .then(({ data }) => {
        setDataTable((prev: any) => {
          const newRecord = {
            _id: data.InsertedID,
            ...payload
          }
          return [newRecord, ...prev]
        });
        message.success("Added new product list URL successfully")
      })
      .catch(() => message.error("Cannot add new product list URL"))
      .finally(() => {
        setLoading(false)
        setProductListUrl("")
      })
  }

  const handleOnDelete = (item: TProductListUrlState) => {
    setLoading(true)
    const id = item._id
    return deleteProductListUrl(id, marketplace as string)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== id);
        setDataTable(newData);
        message.success("Deleted the product list URL successfully")
      })
      .catch(() => message.error("Cannot delete the product list URL"))
      .finally(() => setLoading(false))
  }

  const handleOpenEditProductListUrlModal = (item: any) => {
    setUpdatedProductListUrl(item)
    setOpenEditProductListModal(true)
  }

  const handleOnEditProductListUrl = () => {
    setLoading(true)
    setOpenEditProductListModal(false)
    const payload = {
      _id: updatedProductListUrl._id,
      url: updatedProductListUrl.url,
    }
    return updateProductListUrl(marketplace as string, payload)
      .then(({ data }) => {
        setDataTable((prev: any) => {
          const newDataTable = prev.map((item: TProductListUrlState) => item._id === updatedProductListUrl._id ? ({ ...item, url: updatedProductListUrl.url }) : item)
          return newDataTable
        });
        message.success("Updated new product list URL successfully")
      })
      .catch(() => message.error("Cannot update"))
      .finally(() => {
        setLoading(false)
        setUpdatedProductListUrl(initialProductListUrlState)
      })
  }

  const handleOpenEditSupplierNameModal = (item: any) => {
    setUpdatedSupplier(item)
    setOpenEditSupplierNameModal(true)
  }

  const handleOnEditSupplierName = () => {
    setLoading(true)
    setOpenEditSupplierNameModal(false)
    const payload = {
      _id: updatedProductListUrl._id,
      name: updatedProductListUrl.url,
    }
    return updateSupplierName(payload)
      .then(({ data }) => {
        setDataTable((prev: any) => {
          const newDataTable = prev.map((item: TProductListUrlState) => item._id === updatedProductListUrl._id ? ({ ...item, url: updatedProductListUrl.url }) : item)
          return newDataTable
        });
        message.success("Updated new product list URL successfully")
      })
      .catch(() => message.error("Cannot update"))
      .finally(() => {
        setLoading(false)
        setUpdatedProductListUrl(initialProductListUrlState)
      })
  }

  const handleModalCancel = () => {
    setOpenEditProductListModal(false)
    setOpenEditSupplierNameModal(false)
  }

  const handleOnSelectSupplier = (value: string) => {
    const selectedObj = suppliers.filter((s) => s.value === value)
    setSelectedSupplier(selectedObj[0])
  }

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      hidden: true
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      filters: suppliers.map((spl) => ({ text: spl.label, value: spl.value })),
      onFilter: (value, record) => record.supplier.indexOf(value as string) === 0,
    },
    {
      title: 'Product List URL',
      dataIndex: 'url',
      key: 'url',
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record: any) => {
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => handleOpenEditProductListUrlModal(record)}>Edit</Button>
            <Popconfirm
              placement="topRight"
              title="Are you sure to delete this product list URL?"
              onConfirm={() => handleOnDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<DeleteFilled />} danger />
            </Popconfirm>
          </Space>
        )
      },
    },
  ];

  // Will refactor
  const RenderInputAdd = () => (
    <Row gutter={8}>
      <Col span={4}>
        <label>Product List URL (Category URL):</label>
      </Col>
      <Col span={6}>
        <Input
          value={productListUrl}
          onChange={(e) =>
            setProductListUrl(e.target.value)
          }
        />
      </Col>
    </Row>
  )

  const RenderInputEditProductListUrl = () => (
    <Row gutter={8}>
      <Col span={6}>
        <label>Product List URL (Category URL):</label>
      </Col>
      <Col span={16}>
        <Input
          value={updatedProductListUrl.url}
          onChange={(e) =>
            setUpdatedProductListUrl(prev => ({
              ...prev,
              url: e.target.value
            }))
          }
        />
      </Col>
    </Row>
  )

  const RenderInputEditSupplierName = () => (
    <Row gutter={8}>
      <Col span={6}>
        <label>Supplier Name (domain):</label>
      </Col>
      <Col span={16}>
        <Input
          value={updatedSupplier.label}
          onChange={(e) =>
            setUpdatedSupplier(prev => ({
              ...prev,
              label: e.target.value
            }))
          }
        />
      </Col>
    </Row>
  )

  return (
    <>
      <Helmet>
        <title>Product list URLs | {marketplace} | Management</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Manage Product list URLs"
        footer={
          <Tabs
            defaultActiveKey={activedTab || marketplace}
            type="card"
            activeKey={activedTab || marketplace}
            onChange={handleOnChangeTab}
            items={
              marketplaces.map(m => (
                {
                  key: m.key,
                  tabKey: m.key,
                  label: m.label,
                  children:
                    (<Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
                      <Row gutter={8}>
                        <Col span={4}>
                          <label>Supplier:</label>
                        </Col>
                        <Col span={6}>
                          <Select
                            showSearch
                            loading={loading}
                            value={selectedSupplier.value}
                            options={suppliers}
                            onChange={handleOnSelectSupplier}
                            style={{ width: 200 }}
                          />
                        </Col>
                        <Col span={4}>
                          <Button onClick={() => handleOpenEditSupplierNameModal(selectedSupplier)}>Edit Supplier Name</Button>
                        </Col>
                      </Row>
                      {RenderInputAdd()}
                      <Button type="primary" onClick={() => handleOnClickAdd()} disabled={loading || !productListUrl}>Add</Button>
                      <Table columns={columns} dataSource={dataTable} loading={loading} scroll={{ x: true }} />
                    </Space>)
                }
              ))
            }
          />
        }
      />
      <Modal
        title="Edit Product List URL"
        width={700}
        open={openEditProductListModal}
        onOk={handleOnEditProductListUrl}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        okButtonProps={{ disabled: Object.values(productListUrl).filter(val => val === '').length > 0 }}
      >
        {RenderInputEditProductListUrl()}
      </Modal>
      <Modal
        title="Edit Supplier Name"
        width={700}
        open={openEditSupplierNameModal}
        onOk={handleOnEditSupplierName}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        okButtonProps={{ disabled: updatedSupplier.label === "" || updatedSupplier.label === null }}
      >
        {RenderInputEditSupplierName()}
      </Modal>
    </>
  )
};

export default ProductListURLs;